import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Input, Text, Card, QueryChips } from '@teals/ui-web';
import { useAppDispatch } from '../../store';
import { setCredentials } from '../../store/slices';
import { useCreateOrganisationMutation, useGetSsoStatusQuery } from '../../store/api/portalApi';
import { useAuth } from '../../hooks/useAuth';
import { refreshSession } from '../../services/cognito';
import { useNavigate } from 'react-router-dom';

type SetupPath = 'personal' | 'company';

const COMPANY_TYPES = [
  { value: 'general', label: 'General business' },
  { value: 'agency', label: 'Agency' },
] as const;

const Page = styled.div<{ $embedded?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${({ $embedded }) => ($embedded ? 'auto' : 'calc(100vh - 64px)')};
  padding: ${({ $embedded }) => ($embedded ? '0' : '2rem 1.5rem')};
`;

const SetupCard = styled(Card)`
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.45;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelectLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  min-height: 44px;
  border: 2px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  color: var(--text-primary);
  background: var(--bg-surface);
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--accent);
  }
`;

const DomainBanner = styled.div`
  padding: 0.75rem 1rem;
  background: var(--accent-subtle, rgba(45, 154, 143, 0.12));
  border: 1px solid var(--accent-muted, rgba(45, 154, 143, 0.35));
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  line-height: 1.5;
`;

const ErrorBanner = styled.div`
  padding: 0.75rem 1rem;
  background: var(--bg-danger-subtle, #fef2f2);
  border: 1px solid var(--border-danger, #fecaca);
  border-radius: 0.5rem;
  color: var(--text-danger, #dc2626);
  font-size: 0.875rem;
`;

function suggestedPersonalName(name?: string, email?: string): string {
  if (name?.trim()) {
    return `${name.trim()}’s workspace`;
  }
  const local = email?.split('@')[0];
  if (local) {
    return `${local}’s workspace`;
  }
  return 'Personal workspace';
}

export interface CreateOrganisationProps {
  embedded?: boolean;
  defaultPath?: SetupPath;
  onCreated?: (orgId: string) => void;
}

const CreateOrganisation: React.FC<CreateOrganisationProps> = ({
  embedded = false,
  defaultPath = 'personal',
  onCreated,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user, token, accessToken, refreshToken } = useAuth();
  const personalSuggestion = useMemo(
    () => suggestedPersonalName(user?.name, user?.email),
    [user?.name, user?.email]
  );
  const [path, setPath] = useState<SetupPath>(defaultPath);
  const [name, setName] = useState(
    defaultPath === 'personal' ? personalSuggestion : ''
  );
  const [description, setDescription] = useState('');
  const [businessType, setBusinessType] = useState<string>(
    defaultPath === 'personal' ? 'individual' : 'general'
  );
  const [errorMessage, setErrorMessage] = useState('');

  const [createOrganisation, { isLoading }] = useCreateOrganisationMutation();
  const { data: ssoStatus } = useGetSsoStatusQuery();
  const showDomainBanner =
    path === 'company' &&
    Boolean(ssoStatus?.emailHost) &&
    ssoStatus?.isConsumerDomain === false;

  useEffect(() => {
    if (path === 'personal' && !name.trim()) {
      setName(personalSuggestion);
    }
  }, [path, personalSuggestion, name]);

  const selectPath = (next: SetupPath) => {
    setPath(next);
    if (next === 'personal') {
      setBusinessType('individual');
      if (!name.trim() || name === personalSuggestion) {
        setName(personalSuggestion);
      }
    } else {
      setBusinessType('general');
      if (name === personalSuggestion) {
        setName('');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const type = path === 'personal' ? 'individual' : businessType;
    setErrorMessage('');
    try {
      const org = await createOrganisation({
        name: name.trim(),
        description: description.trim() || undefined,
        businessType: type,
      }).unwrap();

      const refreshed = await refreshSession();
      if (refreshed && user) {
        dispatch(
          setCredentials({
            user: {
              ...user,
              organizationId: refreshed.user.organizationId || org.id,
            },
            token: refreshed.tokens.idToken,
            accessToken: refreshed.tokens.accessToken,
            refreshToken: refreshed.tokens.refreshToken,
          })
        );
      } else if (user && token) {
        dispatch(
          setCredentials({
            user: { ...user, organizationId: org.id },
            token,
            accessToken: accessToken ?? undefined,
            refreshToken: refreshToken ?? undefined,
          })
        );
      }
      if (onCreated) {
        onCreated(org.id);
      } else {
        navigate('/agents?onboarding=owner', { replace: true });
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String(
              (err as { data?: { message?: string } }).data?.message ||
                'Failed to create organisation.'
            )
          : err instanceof Error
            ? err.message
            : 'Failed to create organisation. Please try again.';
      setErrorMessage(message);
    }
  };

  return (
    <Page $embedded={embedded}>
      <SetupCard padding={embedded ? 0 : 32} elevation={embedded ? 'none' : 'md'}>
        <div>
          <Text variant="h3">Set up your organisation</Text>
          <Subtitle>
            Every Luminafactory account needs an organisation before you can build agents,
            manage applications, or invite team members. Choose how you want to start.
          </Subtitle>
        </div>

        <QueryChips
          chips={[{ label: 'Personal workspace' }, { label: 'Set up a company' }]}
          onSelect={(label: string) =>
            selectPath(label === 'Personal workspace' ? 'personal' : 'company')
          }
        />

        {showDomainBanner && (
          <DomainBanner>
            You’ll create the organisation for <strong>{ssoStatus?.emailHost}</strong>.
            Teammates with that domain can join later.
          </DomainBanner>
        )}

        {errorMessage && <ErrorBanner>{errorMessage}</ErrorBanner>}

        <Form onSubmit={handleSubmit}>
          <Input
            label="Organisation name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={path === 'personal' ? 'Your workspace name' : 'Acme Labs'}
            fullWidth
            required
          />
          {path === 'personal' && (
            <Hint>
              This is still an organisation — just for you. You can create a company later
              from Business Center.
            </Hint>
          )}
          {path === 'company' && (
            <>
              <Input
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does your organisation do?"
                fullWidth
              />
              <SelectWrapper>
                <SelectLabel htmlFor="business-type">Business type</SelectLabel>
                <Select
                  id="business-type"
                  value={businessType}
                  onChange={(e) => setBusinessType(e.target.value)}
                >
                  {COMPANY_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Select>
              </SelectWrapper>
            </>
          )}
          <Button
            type="submit"
            variant="primary"
            disabled={!name.trim() || isLoading}
            fullWidth
          >
            {isLoading
              ? 'Creating...'
              : path === 'personal'
                ? 'Continue'
                : 'Create Organisation'}
          </Button>
        </Form>
      </SetupCard>
    </Page>
  );
};

export default CreateOrganisation;
